import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SubmitSuccessView } from './SubmitSuccessView';
import { AppSelect } from './fields/AppSelect';
import { CategorySelect } from './fields/CategorySelect';
import { EmailInput } from './fields/EmailInput';
import { ImageUpload } from './fields/ImageUpload';
import { NameInput } from './fields/NameInput';
import { RatingSelect } from './fields/RatingSelect';
import { TextInput } from './fields/TextInput';

import { submitFeedback } from '@/api/feedback';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MAX_DESCRIPTION_LENGTH } from '@/constants/feedback';
import { useFeedbackPrefill } from '@/hooks/useFeedbackPrefill';
import { useLogger } from '@/lib/logger';
import { feedbackFormSchema } from '@/types/feedback';
import type { FeedbackFormData } from '@/types/feedback';

export function FeedbackForm() {
  const { t } = useTranslation();
  const prefillData = useFeedbackPrefill();
  const logger = useLogger();

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm<FeedbackFormData>({
    resolver: zodResolver(feedbackFormSchema) as never,
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      description: prefillData.description ?? '',
      category: prefillData.category ?? undefined,
      appId: prefillData.appId ?? undefined,
      rating: prefillData.rating ?? undefined,
      userEmail: prefillData.userEmail ?? '',
      userName: prefillData.userName ?? '',
      images: prefillData.images ?? [],
    },
  });

  const description = watch('description');

  const mutation = useMutation({
    mutationFn: submitFeedback,
    onSuccess: () => {
      reset();
    },
    onError: (error: Error) => {
      logger.error('Submit error:', error);
    },
  });

  const onSubmit = handleSubmit((data: FeedbackFormData) => {
    mutation.mutate(data);
  });

  const getErrorMessage = (errorKey?: string): string | undefined => {
    if (!errorKey) return undefined;
    return t(errorKey);
  };

  const handleReset = () => {
    mutation.reset();
  };

  if (mutation.isSuccess) {
    return <SubmitSuccessView onReset={handleReset} />;
  }

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle>{t('feedback.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-6" noValidate>
          <TextInput
            label={t('form.description.label')}
            placeholder={t('form.description.placeholder')}
            required
            multiline
            characterCount={description?.length || 0}
            maxCharacters={MAX_DESCRIPTION_LENGTH}
            register={register('description')}
            error={getErrorMessage(errors.description?.message)}
            disabled={mutation.isPending}
          />

          <Controller
            name="category"
            control={control}
            render={({ field }) => (
              <CategorySelect
                label={t('form.category.label')}
                required
                value={field.value}
                onChange={field.onChange}
                error={getErrorMessage(errors.category?.message)}
                disabled={mutation.isPending}
              />
            )}
          />

          <Controller
            name="appId"
            control={control}
            render={({ field }) => (
              <AppSelect
                label={t('form.appId.label')}
                required
                value={field.value}
                onChange={field.onChange}
                error={getErrorMessage(errors.appId?.message)}
                disabled={mutation.isPending}
              />
            )}
          />

          <Controller
            name="rating"
            control={control}
            render={({ field }) => (
              <RatingSelect
                label={t('form.rating.label')}
                value={field.value}
                onChange={field.onChange}
                disabled={mutation.isPending}
              />
            )}
          />

          <Controller
            name="images"
            control={control}
            render={({ field }) => (
              <ImageUpload
                label={t('form.images.label')}
                value={field.value || []}
                onChange={field.onChange}
                error={getErrorMessage(errors.images?.message)}
                disabled={mutation.isPending}
              />
            )}
          />

          <EmailInput
            register={register('userEmail')}
            error={getErrorMessage(errors.userEmail?.message)}
            disabled={mutation.isPending}
          />

          <NameInput
            register={register('userName')}
            error={getErrorMessage(errors.userName?.message)}
            disabled={mutation.isPending}
          />

          {mutation.isError && (
            <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20">
              <p className="text-sm text-destructive">
                {mutation.error?.message || t('feedback.submitError')}
              </p>
            </div>
          )}

          <div className="flex gap-3">
            <Button
              type="submit"
              disabled={mutation.isPending}
              className="flex-1"
            >
              {mutation.isPending ? t('common.loading') : t('common.submit')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
